import { openModal } from 'tradera-state/ui/action-creators';
import { types } from './item-actions-helper';
import purchaseOrderActions from '../state/actions/purchase-order';
import myTraderaActions from '../state/actions/my-tradera';
import { SINGLE_FEEDBACK_DIALOG_ID } from '../components/leave-feedback/single-feedback-dialog';
import featureToggles from '../utils/feature-toggles';

const isTakeaway = purchaseOrder => {
    return (
        (purchaseOrder.shipping.selectedShipping &&
            purchaseOrder.shipping.selectedShipping.isTakeaway) ||
        (purchaseOrder.shipping.shippingDiscount &&
            purchaseOrder.shipping.shippingDiscount.isTakeaway)
    );
};

const getId = purchaseOrder => purchaseOrder.purchaseOrderId;

export const soldActions = (
    purchaseOrder,
    listViewType,
    memberMessagingParticipation
) => {
    const actions = [
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Ange samfraktpris',
            getTranslatedLabel: t =>
                purchaseOrder.shipping.shippingDiscount
                    ? t('myt_itemCard_actions_update_combinedshipping_change')
                    : t('myt_itemCard_actions_update_combinedshipping'),
            dispatch: dispatch => {
                dispatch(
                    myTraderaActions.openChangeShipping(
                        purchaseOrder?.purchaseOrderId
                    )
                );
            },
            gaLabel: 'ChangeShippingPriceSold',
            isVisible: () =>
                purchaseOrder.combinedShippingInformation &&
                !purchaseOrder.state.isPaid,
            isPrimaryAction: () =>
                !purchaseOrder.shipping.shippingDiscount &&
                purchaseOrder.transactionIds.length > 1
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.LINK,
            label: 'Orderinformation',
            getTranslatedLabel: t => t('myt_itemCard_actions_orderInformation'),
            url: `/my/sold/buyer-info?purchaseOrderId=${purchaseOrder.purchaseOrderId}`,
            gaLabel: 'BuyerInfo',
            isVisible: () => !!purchaseOrder.purchaseOrderId,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Markera som betald',
            getTranslatedLabel: t => t('myt_itemCard_actions_markAsPaid'),
            dispatch: dispatch =>
                dispatch(
                    purchaseOrderActions.updatePurchaseOrderPaidBySeller(
                        purchaseOrder.purchaseOrderId,
                        listViewType,
                        memberMessagingParticipation,
                        true
                    )
                ),
            gaLabel: 'MarkAsPaid',
            isVisible: () => {
                if (purchaseOrder.state.isPaidWithIntegratedPayment) {
                    return false;
                }
                if (
                    purchaseOrder.combinedShippingInformation &&
                    purchaseOrder.combinedShippingInformation
                        .hasBuyerRequestedCombinedShippingPrice &&
                    !purchaseOrder.shipping.shippingDiscount
                ) {
                    return false;
                }
                return !purchaseOrder.state.isPaid;
            },
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.LINK,
            label: 'Boka frakt',
            getTranslatedLabel: t => t('myt_itemCard_actions_bookShipping'),
            url: `/my/shipping?purchaseOrderId=${purchaseOrder.purchaseOrderId}&originUrl=%2fmy%2fsold`,
            gaLabel: 'BookShipping',
            isVisible: () => {
                if (
                    purchaseOrder.state.isShippingBooked ||
                    purchaseOrder.state.isShipped ||
                    purchaseOrder.state.hasUnpaidShipping ||
                    purchaseOrder.state.isArchived ||
                    (purchaseOrder.combinedShippingInformation &&
                        purchaseOrder.combinedShippingInformation
                            .hasBuyerRequestedCombinedShippingPrice &&
                        !purchaseOrder.shipping.shippingDiscount)
                ) {
                    return false;
                }
                return true;
            },
            isPrimaryAction: () => {
                return (
                    !purchaseOrder.state.hasUnpaidShipping &&
                    !isTakeaway(purchaseOrder) &&
                    !purchaseOrder.state.isArchived
                );
            }
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.LINK,
            label: 'Betala frakt',
            getTranslatedLabel: t => t('myt_itemCard_actions_payShipping'),
            url:
                purchaseOrder.shipping?.unpaidShipping?.url ||
                `/my/shipping/checkout`,
            gaLabel: 'PayShipping',
            isVisible: () => purchaseOrder.state.hasUnpaidShipping,
            isPrimaryAction: () => purchaseOrder.state.hasUnpaidShipping
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Avbryt fraktbokning',
            getTranslatedLabel: t => t('myt_itemCard_actions_deleteShipping'),
            dispatch: dispatch => {
                dispatch(
                    purchaseOrderActions.deleteShipment(
                        purchaseOrder.shipping.unpaidShipping.id,
                        purchaseOrder.purchaseOrderId,
                        listViewType
                    )
                );
            },
            gaLabel: 'DeleteShipping',
            isVisible: () => purchaseOrder.state.hasUnpaidShipping,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Avmarkera som bokad',
            getTranslatedLabel: t =>
                t('myt_itemCard_actions_unmarkAsShippingBooked'),
            dispatch: dispatch => {
                dispatch(
                    purchaseOrderActions.updateShippingBooked(
                        purchaseOrder.purchaseOrderId,
                        listViewType,
                        memberMessagingParticipation
                    )
                );
            },
            gaLabel: 'UnmarkAsShippingBooked',
            isVisible: () => {
                return (
                    !purchaseOrder.shipping.shipping?.url &&
                    purchaseOrder.state.isShippingBooked &&
                    !purchaseOrder.state.isShipped
                );
            },
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.LINK,
            label: 'Spåra paket',
            getTranslatedLabel: t => t('myt_itemCard_actions_trackShipment'),
            url: `/my/sold/buyer-info?purchaseOrderId=${purchaseOrder.purchaseOrderId}#shipping`,
            gaLabel: 'TrackShipment',
            isVisible: () => !!purchaseOrder.shipping.shipping?.url,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.EXTERNAL_LINK,
            label: 'Kvitto för frakt',
            getTranslatedLabel: t => t('myt_itemCard_actions_shippingReceipt'),
            url: `/my/shipping/receipt/${purchaseOrder.shipping.shipping?.shipmentCheckoutId}`,
            gaLabel: 'ShippingReceipt',
            isVisible: () =>
                purchaseOrder.shipping.shipping?.hasShipmentReceipt,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.EXTERNAL_LINK,
            label: 'Ladda ner fraktsedel',
            getTranslatedLabel: t => t('myt_itemCard_actions_shippingLabel'),
            url: `/my/shipping/label/${purchaseOrder.shipping.shipping?.shipmentId}`,
            gaLabel: 'ShippingLabel',
            isVisible: () => purchaseOrder.shipping.shipping?.hasShipmentLabel,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: isTakeaway(purchaseOrder)
                ? 'Avmarkera som hämtad'
                : 'Avmarkera som skickad',
            getTranslatedLabel: t =>
                isTakeaway(purchaseOrder)
                    ? t('myt_itemCard_actions_unmarkAsCollected')
                    : t('myt_itemCard_actions_unmarkAsSent'),
            dispatch: dispatch => {
                dispatch(
                    purchaseOrderActions.updatePurchaseOrderShipped(
                        purchaseOrder.purchaseOrderId,
                        listViewType,
                        purchaseOrder.transactionIds,
                        false
                    )
                );
            },
            gaLabel: 'UnmarkAsShipped',
            isVisible: () => purchaseOrder.state.isShipped,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: isTakeaway(purchaseOrder)
                ? 'Markera som hämtad'
                : 'Markera som skickad',
            getTranslatedLabel: t =>
                isTakeaway(purchaseOrder)
                    ? t('myt_itemCard_actions_markAsCollected')
                    : t('myt_itemCard_actions_markAsSent'),
            dispatch: dispatch => {
                dispatch(
                    purchaseOrderActions.updatePurchaseOrderShipped(
                        purchaseOrder.purchaseOrderId,
                        listViewType,
                        purchaseOrder.transactionIds,
                        true
                    )
                );
            },
            gaLabel: 'MarkAsShipped',
            isVisible: () => {
                return !purchaseOrder.state.isShipped;
            },
            isPrimaryAction: () => !purchaseOrder.state.hasUnpaidShipping
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Lämna omdöme',
            getTranslatedLabel: t => t('myt_itemCard_actions_leaveFeedback'),
            dispatch: dispatch => {
                dispatch(
                    openModal({
                        modalId: SINGLE_FEEDBACK_DIALOG_ID,
                        data: {
                            purchaseOrderId: purchaseOrder.purchaseOrderId,
                            transactionIds: purchaseOrder.itemsToGiveFeedBackTo
                        }
                    })
                );
            },
            gaLabel: 'LeaveFeedbackOnBuyer',
            isVisible: () => !!purchaseOrder.itemsToGiveFeedBackTo.length,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Återbetala',
            getTranslatedLabel: t => t('myt_itemCard_actions_refund'),
            dispatch: dispatch =>
                dispatch(
                    myTraderaActions.openSetDiscount(
                        purchaseOrder.purchaseOrderId
                    )
                ),
            gaLabel: 'Refund',
            isVisible: () => purchaseOrder.state.isRefundAllowed,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.LINK,
            label: 'Köparens omdömen',
            getTranslatedLabel: t => t('myt_itemCard_actions_buyerFeedback'),
            url: `/profile/feedback/${purchaseOrder.buyerMember.memberId}`,
            gaLabel: 'BuyerProfile',
            isVisible: () => true,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Avmarkera som betald',
            getTranslatedLabel: t => t('myt_itemCard_actions_unmarkAsPaid'),
            dispatch: dispatch =>
                dispatch(
                    purchaseOrderActions.updatePurchaseOrderPaidBySeller(
                        purchaseOrder.purchaseOrderId,
                        listViewType,
                        memberMessagingParticipation,
                        false
                    )
                ),
            gaLabel: 'UnmarkAsPaid',
            isVisible: () => {
                if (purchaseOrder.state.isPaidWithIntegratedPayment) {
                    return false;
                }
                return !!purchaseOrder.state.isPaid;
            },
            isPrimaryAction: () => true
        }),
        () => ({
            // "UPI" = "Unpaid Item"
            id: getId(purchaseOrder),
            type: types.LINK,
            label: 'Anmäl utebliven betalning',
            getTranslatedLabel: t => t('myt_itemCard_actions_reportUnpaidItem'),
            url: `/resolution/UPI2/${purchaseOrder.purchaseOrderId}`,
            gaLabel: 'ReportUnpaidItem',
            isVisible: () =>
                featureToggles.isUpiPurchaseOrderEnabled &&
                !purchaseOrder.state.isPaid &&
                !purchaseOrder.state.isUpiSubmitted,
            isPrimaryAction: () => true
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Lämna rabatt',
            getTranslatedLabel: t => t('myt_itemCard_actions_giveDiscount'),
            dispatch: dispatch =>
                dispatch(
                    myTraderaActions.openSetDiscount(
                        purchaseOrder.purchaseOrderId
                    )
                ),
            gaLabel: 'Discount',
            isVisible: () => purchaseOrder.state.isDiscountAllowed,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Dölj',
            getTranslatedLabel: t => t('myt_itemCard_actions_hide'),
            dispatch: dispatch =>
                dispatch(
                    purchaseOrderActions.updatePurchaseOrderHiddenForSeller(
                        purchaseOrder.purchaseOrderId,
                        true,
                        listViewType
                    )
                ),
            gaLabel: 'HideSold',
            isVisible: () => !purchaseOrder.state.isHiddenForSeller,
            isPrimaryAction: () => false
        }),
        () => ({
            id: getId(purchaseOrder),
            type: types.DISPATCH,
            label: 'Ångra dölj',
            getTranslatedLabel: t => t('myt_itemCard_actions_unhide'),
            dispatch: dispatch =>
                dispatch(
                    purchaseOrderActions.updatePurchaseOrderHiddenForSeller(
                        purchaseOrder.purchaseOrderId,
                        false,
                        listViewType
                    )
                ),
            gaLabel: 'UnhideSold',
            isVisible: () => purchaseOrder.state.isHiddenForSeller,
            isPrimaryAction: () => false
        })
    ];

    return actions;
};
